<script lang="ts" setup>
import { Subscription } from "rxjs";

const isUpdating = ref(false);

const logError = ref(false);

const documentId = inject("documentId") as any;
const documentSelectionStore = useDocumentSelectionStore(documentId);
const { hasSelection, endPosition, selectedTokens } = storeToRefs(
  documentSelectionStore
);

const documentViewModeStore = useDocumentViewMode(documentId)();
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const subscriber = ref<Subscription | undefined>();

const documentActiveTypeStore = useDocumentActiveTypeStore(documentId);
const { hasActiveTypes, activeSubTypes, activeTypes, activeCriteria } =
  storeToRefs(documentActiveTypeStore);

const clearTypes = async () => {
  try {
    if (activeTypes.value.includes("sentenceOpener")) {
      useBaseAlert(
        "Coming soon!",
        "Adding sentence openers is not supported yet."
      );
      return;
    }

    isUpdating.value = true;

    const evaluateResultsStore = useStepResultsStore(documentId);
    const { evaluationResultsObservable, isLoadingResults } =
      storeToRefs(evaluateResultsStore);

    const tokensToSave = [] as ParagraphTokens;

    for (var token of selectedTokens.value) {
      if (token == undefined) continue;

      token.indicators = {
        types: [],
        subTypes: [],
        markers: [],
      };

      token.formatting.italic = false;
      token.formatting.underline = false;

      tokensToSave.push(token);
    }

    if (activeTypes.value.includes("sentenceOpener")) {
      await useDocumentParagraphsStore(documentId).reparseParagraphs();
    } else {
      await useDocumentParagraphsStore(documentId).saveTokens(tokensToSave);
    }

    useDocumentHighlightStore(documentId).clearAll();

    evaluateResultsStore.reevaluate(true);

    subscriber.value = evaluationResultsObservable.value.subscribe(() => {
      if (isLoadingResults.value != true && logError.value == true) {
        // Get instance from token
        // reportProbem with that instance
        const relevantInstance = evaluateResultsStore.getInstanceFromToken(
          tokensToSave[0]
        );

        if (relevantInstance) {
          evaluateResultsStore.reportProblem(
            {
              relevantInstance: relevantInstance,
            },
            false,
            "Infer did not work"
          );
        }

        subscriber.value?.unsubscribe();
      }

      logError.value = true;
    });
  } catch (error) {}
  isUpdating.value = false;
};

const addSubType = async () => {
  try {
    isUpdating.value = true;

    const evaluateResultsStore = useStepResultsStore(documentId);
    const { evaluationResultsObservable, isLoadingResults } =
      storeToRefs(evaluateResultsStore);

    const tokensToSave = [] as ParagraphTokens;

    for (var token of selectedTokens.value) {
      if (token == undefined) continue;

      if (
        activeTypes.value.includes("dressUp") ||
        activeTypes.value.includes("decoration")
      ) {
        if (token.indicators == undefined) {
          token.indicators = {
            types: activeTypes.value,
            subTypes: [],
            markers: [],
          };
        }
        if (token.indicators.types == undefined) {
          token.indicators.types = activeTypes.value;
        }

        if (token.indicators.subTypes == undefined) {
          token.indicators.subTypes = [];
        }

        token.indicators.types = [
          ...new Set([...token.indicators.types, ...activeTypes.value]),
        ];
        token.indicators.subTypes = activeSubTypes.value;
      }

      if (activeTypes.value.includes("dressUp")) {
        token.formatting.underline = true;
        tokensToSave.push(token);
      } else if (activeTypes.value.includes("sentenceOpener")) {
        //  Ok so wht I'm going to do is map the subtype to marker then inject the market and try that out.

        let marker: string | undefined = undefined;

        if (activeSubTypes.value.includes("subject")) {
          marker = "[1] ";
        } else if (activeSubTypes.value.includes("prepositional")) {
          marker = "[2] ";
        } else if (activeSubTypes.value.includes("lyAdverb")) {
          marker = "[3] ";
        } else if (activeSubTypes.value.includes("ing")) {
          marker = "[4] ";
        } else if (activeSubTypes.value.includes("clausal")) {
          marker = "[5] ";
        } else if (activeSubTypes.value.includes("vss")) {
          marker = "[6] ";
        }

        if (marker) {
          var markerToken = {
            type: "marker",
            text: marker,
            formatting: {},
            // indicators: {
            //   types: ["sentenceOpener"],
            //   subTypes: activeSubTypes.value,
            // },
          } as ParagraphToken;

          const sentence = useDocumentEvaluativeParagraphsStore(
            documentId
          ).sentenceFromTokenId(token.id);

          if (sentence == undefined) continue;

          sentence.tokens.sort((a, b) => a.position - b.position);

          // fet the first token where the type is not filler:

          const sentenceTokens: ParagraphTokens = sentence.tokens
            .map(
              (t) =>
                useDocumentTokenIdPositionMapStore(documentId).tokenStoreFromId(
                  t.id
                ).token
            )
            .filter((t) => t != undefined);

          const senetenceTokenPositions = sentenceTokens.map(
            (t) => t?.globalPosition
          );

          // Next we want to add the indicators to the first non fillter token
          const firstNonFillerToken = sentenceTokens.find(
            (t) => t?.type != "filler"
          );

          if (firstNonFillerToken == undefined) continue;

          firstNonFillerToken.indicators = {
            types: ["sentenceOpener"],
            subTypes: activeSubTypes.value,
            markers: [],
          };

          // Now we need to save teh paragraph that this token belongs to:
          const firstNonFillerTokenStore = useParagraphTokenStore(
            documentId,
            firstNonFillerToken.globalPosition
          );

          if (firstNonFillerTokenStore == undefined) continue;

          const firstNonFillerParagraphId =
            firstNonFillerTokenStore.paragraphId;

          if (firstNonFillerParagraphId == undefined) continue;

          const firstNonFillerParagraph = useDocumentParagraphStore(
            documentId,
            firstNonFillerParagraphId
          )?.paragraph;

          if (firstNonFillerParagraph == undefined) continue;

          await useDocumentParagraphsStore(documentId).saveParagraph(
            firstNonFillerParagraph
          );

          const firstTokenPosition = senetenceTokenPositions[0];

          const tokenStore = useParagraphTokenStore(
            documentId,
            firstTokenPosition
          );

          if (tokenStore == undefined) continue;

          const paragraphId = tokenStore.paragraphId;

          if (paragraphId == undefined) continue;

          const revisionId = useSubmittedDocumentStore(documentId).revision?.id;

          if (revisionId == undefined) continue;

          await $fetch(
            `/api/${documentId}/${revisionId}/paragraphs/add_token`,
            {
              method: "POST",
              headers: await useApiHeaders(),
              body: {
                token: markerToken,
                position: firstTokenPosition,
                paragraphId: paragraphId,
              },
            }
          );
        }
      } else if (activeTypes.value.includes("decoration")) {
        token.formatting.italic = true;
        tokensToSave.push(token);
      }
    }

    if (activeTypes.value.includes("sentenceOpener")) {
      await useDocumentParagraphsStore(documentId).reparseParagraphs();
    } else {
      await useDocumentParagraphsStore(documentId).saveTokens(tokensToSave);
    }

    evaluateResultsStore.reevaluate(true);

    subscriber.value = evaluationResultsObservable.value.subscribe(() => {
      if (isLoadingResults.value != true && logError.value == true) {
        // Get instance from token
        // reportProbem with that instance
        const relevantInstance = evaluateResultsStore.getInstanceFromToken(
          tokensToSave[0]
        );

        if (relevantInstance) {
          evaluateResultsStore.reportProblem(
            {
              relevantInstance: relevantInstance,
            },
            false,
            "Infer did not work"
          );
        }

        subscriber.value?.unsubscribe();
      }

      logError.value = true;
    });
  } catch (error) {}
  isUpdating.value = false;
};

onUnmounted(() => {
  subscriber.value?.unsubscribe();
});

const documentHighlightStore = useDocumentHighlightStore(documentId);
const { isTokenHighlighted } = storeToRefs(documentHighlightStore);

const showAddStyleButton = computed(() => {
  if (hasSelection.value == false || hasActiveTypes.value == false)
    return false;

  const token = useParagraphTokenStore(documentId, endPosition.value!).token;
  var satisfiesUnderlineRequirement =
    isTeacherViewMode.value != true || token?.formatting?.underline == true;

  if (
    activeTypes.value.includes("sentenceOpener") &&
    isTeacherViewMode.value != true
  ) {
    satisfiesUnderlineRequirement = true;
  }

  var satisfiesHighlightRequirement =
    isTokenHighlighted.value(endPosition.value!) != true;

  return satisfiesUnderlineRequirement && satisfiesHighlightRequirement;
});

const topCoordinates = computed(() => {
  const tokenId = useTokenElementIdGenerator().generateTokenElementId(
    endPosition.value!
  );

  const tokenElement = document.getElementById(tokenId);

  if (!tokenElement) return 0;

  const domRect = tokenElement.getBoundingClientRect();

  return domRect.bottom + 8;
});

const containerRef = ref();

const leftCoordinates = computed(() => {
  const tokenId = useTokenElementIdGenerator().generateTokenElementId(
    endPosition.value!
  );

  const tokenElement = document.getElementById(tokenId);

  if (!tokenElement) return 0;

  const domRect = tokenElement.getBoundingClientRect();

  let width = 0;

  const containerElement = containerRef.value as HTMLElement;

  if (containerElement) {
    const containerRect = containerElement.getBoundingClientRect();

    width = containerRect.width;
  }

  return domRect.left - width / 2 + domRect.width / 2;
});

const removeInstructions = computed(() => {
  if (activeCriteria.value?.styleSection == "Dress-Ups") {
    return "Remove underline.";
  } else if (activeCriteria.value?.styleSection == "Sentence Openers") {
    return "Remove number.";
  } else if (activeCriteria.value?.styleSection == "Decorations") {
    return "Remove italics.";
  }
});
</script>

<template>
  <div
    ref="containerRef"
    class="p-2 shadow rounded bg-surface fixed transition-opacity"
    :class="{
      ' opacity-100 pointer-events-auto': showAddStyleButton,
      ' opacity-0 pointer-events-none': showAddStyleButton != true,
    }"
    :style="{
      top: topCoordinates + 'px',
      left: leftCoordinates + 'px',
    }"
  >
    <BaseTextButton
      @click.prevent="
        isTokenHighlighted(endPosition!) != true ? addSubType() : clearTypes()
      "
      :showSpinner="isUpdating"
      class="text-[10px]"
    >
      {{
        isTokenHighlighted(endPosition!) != true
          ? "Identify as " + activeCriteria?.label ??
            activeSubTypes[0] ??
            activeTypes[0]
          : removeInstructions
      }}
    </BaseTextButton>
  </div>
</template>

<style scoped></style>
